// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * Role Management API Service
 * 
 * Handles all API calls for role and permission management.
 */

import { apiClient } from '../config/api';

const BASE_URL = '/api/v1/roles';

const roleManagementService = {
  // ========================================================================
  // Role CRUD Operations
  // ========================================================================
  
  /**
   * Get all roles with statistics
   */
  getRoles: async (includeInactive = false) => {
    const response = await apiClient.get(BASE_URL, {
      params: { include_inactive: includeInactive }
    });
    return response.data;
  },
  
  /**
   * Get single role details with statistics
   */
  getRoleDetails: async (roleId) => {
    const response = await apiClient.get(`${BASE_URL}/${roleId}`);
    return response.data;
  },
  
  /**
   * Create new custom role
   */
  createRole: async (roleData) => {
    const response = await apiClient.post(BASE_URL, roleData);
    return response.data;
  },
  
  /**
   * Update role details (not system roles)
   */
  updateRole: async (roleId, roleData) => {
    const response = await apiClient.put(`${BASE_URL}/${roleId}`, roleData);
    return response.data;
  },
  
  /**
   * Delete custom role (cannot delete system roles or roles with users)
   */
  deleteRole: async (roleId) => {
    const response = await apiClient.delete(`${BASE_URL}/${roleId}`);
    return response.data;
  },
  
  /**
   * Duplicate existing role with all permissions
   */
  duplicateRole: async (roleId, newRoleData) => {
    const response = await apiClient.post(
      `${BASE_URL}/${roleId}/duplicate`,
      newRoleData
    );
    return response.data;
  },
  
  // ========================================================================
  // Permission Matrix Operations
  // ========================================================================
  
  /**
   * Get role permissions organized by category for matrix UI
   */
  getPermissionMatrix: async (roleId) => {
    const response = await apiClient.get(
      `${BASE_URL}/${roleId}/permissions/matrix`
    );
    return response.data;
  },
  
  /**
   * Bulk update role permissions
   */
  bulkUpdatePermissions: async (roleId, updates) => {
    const response = await apiClient.put(
      `${BASE_URL}/${roleId}/permissions`,
      { permissions: updates }
    );
    return response.data;
  }
};

export default roleManagementService;
